import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Target, Eye, Users, CheckCircle, Sparkles, ArrowRight, GraduationCap } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Breadcrumb from '../components/Breadcrumb';
import ProspectiveStudentsModal from '../components/ProspectiveStudentsModal';

const AboutPage: React.FC = () => {
  const [showProspectiveModal, setShowProspectiveModal] = useState(false);

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <div className="h-16" />
      <Breadcrumb />
      <ProspectiveStudentsModal open={showProspectiveModal} onClose={() => setShowProspectiveModal(false)} />

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Hero Section */}
        <div className="text-center mb-16">
          <h1 className="text-4xl font-bold text-gray-900 mb-6">
            인간-AI 협력 연구센터 소개
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            인간의 창의성과 AI의 계산 능력을 결합하여 새로운 가치를 창출하고,
            사회적 문제를 해결하는 혁신적인 연구를 수행합니다.
          </p>
        </div>

        {/* Prospective Students Card */}
        <section className="mb-16">
          <button
            onClick={() => setShowProspectiveModal(true)}
            className="group w-full text-left relative overflow-hidden rounded-2xl bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-500 p-[2px] shadow-xl hover:shadow-2xl transition-all"
          >
            <div className="bg-white rounded-[14px] p-6 md:p-8">
              <div className="flex flex-col md:flex-row items-start md:items-center gap-5">
                <div className="flex-shrink-0">
                  <div className="bg-gradient-to-br from-indigo-500 to-purple-600 w-16 h-16 rounded-2xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                    <GraduationCap className="h-8 w-8 text-white" />
                  </div>
                </div>
                <div className="flex-1">
                  <div className="inline-flex items-center gap-1.5 bg-gradient-to-r from-indigo-100 to-purple-100 text-indigo-700 px-3 py-1 rounded-full text-xs font-semibold mb-2">
                    <Sparkles className="h-3 w-3" />
                    FOR NEWCOMERS
                  </div>
                  <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-1.5 group-hover:text-indigo-600 transition-colors">
                    대학(원) 신입생들을 위한 연구센터 소개
                  </h2>
                  <p className="text-gray-600 text-sm md:text-base">
                    "AI 시대, 어떤 대학을 선택해야 할까?" — 서울여대 CHAIR가 신입생 여러분에게 들려주고 싶은 이야기
                  </p>
                </div>
                <div className="flex-shrink-0 flex items-center gap-2 text-indigo-600 font-semibold text-sm md:text-base group-hover:translate-x-1 transition-transform">
                  자세히 보기
                  <ArrowRight className="h-4 w-4" />
                </div>
              </div>
            </div>
          </button>
        </section>

        {/* Logo and Symbolic Meaning Section */}
        <section className="bg-white rounded-lg p-8 shadow-sm mb-16">
          <div className="text-center mb-8">
            <img
              src="/chair-logo.png"
              alt="HUMAN-AI COLLABORATION CENTER"
              className="max-h-48 max-w-full w-auto mx-auto mb-6 object-contain"
            />
            <h2 className="text-3xl font-bold text-gray-900 mb-4">센터 로고의 의미</h2>
            <p className="text-lg text-gray-600 max-w-4xl mx-auto">
              우리 센터의 로고는 인간과 AI의 협력을 상징하며, 4개 연구 분과의 통합적 연구 영역을 형상화합니다.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8">
            <div className="space-y-6">
              <div className="flex items-start">
                <div className="bg-teal-50 w-12 h-12 rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                  <div className="w-6 h-6 bg-teal-500 rounded-full"></div>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">인간의 지능</h3>
                  <p className="text-gray-600">
                    로고의 왼쪽 절반은 인간의 뇌를 형상화하여 창의성, 직관, 감정적 지능을 상징합니다.
                  </p>
                </div>
              </div>
              
              <div className="flex items-start">
                <div className="bg-teal-100 w-12 h-12 rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                  <div className="w-6 h-6 bg-teal-600 rounded-full"></div>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">AI의 기술</h3>
                  <p className="text-gray-600">
                    로고의 오른쪽 절반은 회로 패턴을 형상화하여 계산 능력, 데이터 처리, 알고리즘을 상징합니다.
                  </p>
                </div>
              </div>
            </div>
            
            <div className="space-y-6">
              <div className="flex items-start">
                <div className="bg-purple-100 w-12 h-12 rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                  <div className="w-6 h-6 bg-purple-600 rounded-full"></div>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">4개 연구 분과</h3>
                  <p className="text-gray-600">
                    중앙의 4개 연결된 노드는 우리 센터의 4개 연구 분과를 상징하며, 상호 연계된 연구를 나타냅니다.
                  </p>
                </div>
              </div>
              
              <div className="flex items-start">
                <div className="bg-orange-100 w-12 h-12 rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                  <div className="w-6 h-6 bg-orange-600 rounded-full"></div>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">협력과 통합</h3>
                  <p className="text-gray-600">
                    인간과 AI의 경계를 넘나드는 연결선은 상호 보완적 협력과 통합적 연구 접근을 의미합니다.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Mission, Vision, Values */}
        <div className="grid md:grid-cols-3 gap-8 mb-16">
          <div className="bg-white rounded-lg p-8 shadow-sm">
            <div className="text-center">
              <Target className="h-12 w-12 text-teal-600 mx-auto mb-4" />
              <h3 className="text-2xl font-bold text-gray-900 mb-4">미션</h3>
              <p className="text-gray-600">
                인간과 AI의 협력을 통해 사회적 가치를 창출하고, 
                윤리적이고 책임감 있는 AI 기술 발전을 선도합니다.
              </p>
            </div>
          </div>

          <div className="bg-white rounded-lg p-8 shadow-sm">
            <div className="text-center">
              <Eye className="h-12 w-12 text-teal-600 mx-auto mb-4" />
              <h3 className="text-2xl font-bold text-gray-900 mb-4">비전</h3>
              <p className="text-gray-600">
                인간과 AI가 상호 보완적으로 협력하여 
                더 나은 미래를 만들어가는 글로벌 리더십 센터가 되겠습니다.
              </p>
            </div>
          </div>

          <div className="bg-white rounded-lg p-8 shadow-sm">
            <div className="text-center">
              <Users className="h-12 w-12 text-teal-600 mx-auto mb-4" />
              <h3 className="text-2xl font-bold text-gray-900 mb-4">핵심 가치</h3>
              <p className="text-gray-600">
                협력, 혁신, 윤리, 포용성을 바탕으로 
                인간 중심의 AI 기술 발전을 추구합니다.
              </p>
            </div>
          </div>
        </div>

        {/* Establishment Purpose */}
        <section className="bg-white rounded-lg p-8 shadow-sm mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">설립 취지</h2>
          <div className="prose max-w-none">

            {/* 핵심 긴장감 */}
            <div className="bg-navy-950 text-white rounded-xl p-7 mb-8">
              <p className="text-base leading-relaxed text-gray-300 mb-4">
                AI 기술은 빠르게 발전합니다. 하지만 그 속도만큼 중요한 질문이 남겨지고 있습니다.
              </p>
              <div className="space-y-3">
                <div className="flex items-start">
                  <span className="text-amber-400 mr-3 text-lg leading-none mt-0.5">—</span>
                  <p className="text-gray-300 text-sm leading-relaxed">
                    AI 시스템은 우리의 검색 기록, 위치, 행동 패턴을 수집합니다. 대부분의 사람들은 이 사실을 읽지 않은 약관 속에서 이미 동의했습니다.
                  </p>
                </div>
                <div className="flex items-start">
                  <span className="text-amber-400 mr-3 text-lg leading-none mt-0.5">—</span>
                  <p className="text-gray-300 text-sm leading-relaxed">
                    AI 기업들은 개인정보를 보호하겠다고 선언하면서, 동시에 그 정보로 모델을 학습시켜 수익을 창출합니다. 이 구조적 모순은 기업의 악의가 아니라 <strong className="text-white">비즈니스 모델 자체에서 비롯됩니다.</strong>
                  </p>
                </div>
                <div className="flex items-start">
                  <span className="text-amber-400 mr-3 text-lg leading-none mt-0.5">—</span>
                  <p className="text-gray-300 text-sm leading-relaxed">
                    AI의 발전 속도는 인간이 그 함의를 이해하고 사회적 합의를 도출하는 속도를 훨씬 앞서가고 있습니다.
                  </p>
                </div>
              </div>
            </div>

            <p className="text-lg text-gray-700 mb-5 leading-relaxed">
              CHAIR는 이 긴장감에서 출발합니다. AI를 무조건 환영하거나, 무조건 경계하는 것이 아니라 —
              <strong> 충분한 인식(awareness) 위에서 인간의 속도로 협력하는 방법을 연구합니다.</strong>
            </p>

            <p className="text-base text-gray-600 mb-6 leading-relaxed">
              진정한 협력은 어려운 질문을 피하지 않는 것에서 시작됩니다.
              AI가 무엇을 수집하는지, 어떻게 작동하는지, 누구의 이익을 위해 설계되었는지를 먼저 이해해야
              비로소 대등한 협력이 가능합니다. 우리는 이런 인식 기반 위에서 다음을 추구합니다:
            </p>

            <ul className="space-y-3 text-gray-600">
              <li className="flex items-start">
                <CheckCircle className="h-5 w-5 text-teal-600 mr-3 mt-1 flex-shrink-0" />
                <span>AI 기술이 무엇인지, 무엇을 하는지에 대한 사회적 인식 제고</span>
              </li>
              <li className="flex items-start">
                <CheckCircle className="h-5 w-5 text-teal-600 mr-3 mt-1 flex-shrink-0" />
                <span>검증 가능한 신뢰 — 선언이 아닌 구조에서 오는 AI 윤리·책임성 연구</span>
              </li>
              <li className="flex items-start">
                <CheckCircle className="h-5 w-5 text-teal-600 mr-3 mt-1 flex-shrink-0" />
                <span>인간의 속도에 맞춘 AI 협력 모델 개발 — 효율보다 이해를 우선</span>
              </li>
              <li className="flex items-start">
                <CheckCircle className="h-5 w-5 text-teal-600 mr-3 mt-1 flex-shrink-0" />
                <span>다양한 분야의 전문가·시민이 참여하는 다학제적 협력 연구</span>
              </li>
              <li className="flex items-start">
                <CheckCircle className="h-5 w-5 text-teal-600 mr-3 mt-1 flex-shrink-0" />
                <span>한국 사회에 맞는 현실적·실효적 AI 거버넌스 모델 제안</span>
              </li>
            </ul>
          </div>
        </section>

        {/* Research Areas */}
        <section className="bg-white rounded-lg p-8 shadow-sm mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">주요 연구 영역</h2>
          <div className="space-y-8">
            <div className="border-l-4 border-teal-400 pl-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-3">1분과: AI 윤리·신뢰성·책임성 분과</h3>
              <p className="text-gray-600 leading-relaxed">
                AI 윤리기준 프레임워크 및 강령 개발, AI 알고리즘의 편향 검출과 공정성 분석, AI 리터러시 교육 커리큘럼 개발, AI 저작권 및 개인정보 보호 가이드라인 수립을 통해 인간 중심의 신뢰 기반 AI 생태계를 구축한다.
              </p>
            </div>
            <div className="border-l-4 border-orange-500 pl-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-3">2분과: AI 융합교육 개발 분과</h3>
              <p className="text-gray-600 leading-relaxed">
                국내외 AX융합형 교과목 트렌드 분석, 자연과학·인문학·사회학·기술경영 등 주요 전공 분야 및 교양 영역의 신규 융합 교과목 개발(3~5개), AX기반 교육 인프라 활용 방안 제안을 통해 AI 전환 시대 융합형 인재를 양성한다.
              </p>
            </div>
            <div className="border-l-4 border-purple-500 pl-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-3">3분과: 지산학 연계 분과</h3>
              <p className="text-gray-600 leading-relaxed">
                산업·지역 현안 기반 AI 응용 연구 주제 발굴, 산업체 요구 중심 교육 콘텐츠 개발, AI 기반 캡스톤 프로젝트 및 산학 공동 워크숍 운영, 기술 사업화 검토를 통해 현장 중심의 인간-AI 협력 모델을 구축하고 실무형 인재를 양성한다.
              </p>
            </div>
            <div className="border-l-4 border-yellow-500 pl-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-3">4분과: AI 교수학습 분과</h3>
              <p className="text-gray-600 leading-relaxed">
                인간-AI 협력 개념의 철학적·인지적 기반 정립, 협력의 핵심 구인(인지적·의사결정·창의적·신뢰 협력) 정의 및 영향 관계 분석, 학문 분야별 협력 양상 비교 연구, 협력 수준별 교수학습 가이드라인 도출을 통해 공동지성 기반 교수학습 패러다임을 구축한다.
              </p>
            </div>
          </div>
        </section>

        {/* Call to Action */}
        <section className="text-center bg-teal-50/60 rounded-lg p-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            함께 만들어가는 인간-AI 협력의 미래
          </h2>
          <p className="text-lg text-gray-600 mb-6">
            인간과 AI가 함께 성장하는 미래를 위해 여러분의 참여를 기다립니다.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/divisions" 
              className="inline-flex items-center px-6 py-3 bg-teal-500 text-white font-medium rounded-lg hover:bg-teal-600 transition-colors"
            >
              연구 분과 알아보기
            </Link>
            <Link 
              to="/faculty" 
              className="inline-flex items-center px-6 py-3 bg-white text-teal-600 font-medium rounded-lg border border-teal-500 hover:bg-teal-50/60 transition-colors"
            >
              교수진 소개 보기
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default AboutPage;
